import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getServiceRequestPdfById } from 'src/services/client';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { updateBulkStatus } from 'src/store/actions/serviceRequests';
import {
  useModulePermissionsAPI,
  UseModulePermissionsAPIReturn,
} from 'src/SharedCustomHooks/UseModulePermissionsAPI/useModulePermissionsAPI';
import { ModulePermission } from 'src/components/ContactForm/types';
import {
  useServiceRequestAPI,
  UseServiceRequestAPIReturn,
} from 'src/pages/ServiceRequest/CustomHooks/useServiceRequestAPI';
import {
  useServiceRequestSubscribers,
  Subscriber,
  SaveSubscribersResult,
  UseServiceRequestSubscribersReturn,
} from 'src/pages/ServiceRequest/Subscribers/CustomHooks/useServiceRequestSubscribers';
import {
  useTicketStatuses,
  TicketStatus,
  UseTicketStatusesReturn,
} from 'src/pages/ServiceRequest/CustomHooks/useTicketStatuses';
import { IServiceRequestDetail } from 'src/services/api/ServiceRequest/apiContracts';

const HOME_OWNER_DATA_VISIBILITY_MODULE: string = 'Home Owner Data Visibility';

interface ServiceRequestUIState {
  showEditOverlay: boolean;
  showAssignOverlay: boolean;
  showScheduleOverlay: boolean;
  showDeclinePopup: boolean;
}

export interface UseServiceRequestPageOrchestratorArgs {
  serviceRequestId: string;
  onAfterDelete: () => void;
}

export interface UseServiceRequestPageOrchestratorReturn {
  user: any;
  serviceRequest: IServiceRequestDetail | null;
  activity: any[];
  loading: boolean;
  error: string | null;
  oneYearWarrantyIssues: any;
  homeOwnerDataVisibility: boolean;

  showEditOverlay: boolean;
  showAssignOverlay: boolean;
  showScheduleOverlay: boolean;
  showDeclinePopup: boolean;

  openEditOverlay: () => void;
  closeEditOverlay: () => void;
  closeAssignOverlay: () => void;
  closeScheduleOverlay: () => void;

  refreshData: () => Promise<void>;
  fetchServiceRequestById: (id: string) => Promise<any>;
  handleDeleteServiceRequest: () => Promise<void>;
  handleStatusChange: (statusFormData: any, srId: string) => Promise<void>;
  handleStatusAssign: (statusFormData: any, srId: string) => Promise<void>;
  onSubmitDeclinePopup: (type: string, data: any) => Promise<void>;
  handleEditFormSaved: () => void;
  handleSaveScheduledAppointment: (
    formData: any,
    serviceRequestId: string
  ) => Promise<void>;
  downloadPdf: (id: string) => void;

  subscribers: Subscriber[];
  subscribersLoading: boolean;
  subscribersSaving: boolean;
  saveSubscribers: (userIds: string[]) => Promise<SaveSubscribersResult>;
  isCurrentUserSubscribed: boolean;
}

export const useServiceRequestPageOrchestrator = ({
  serviceRequestId,
  onAfterDelete,
}: UseServiceRequestPageOrchestratorArgs): UseServiceRequestPageOrchestratorReturn => {
  const user: any = useSelector((state: any) => selectAuthenticatedUser(state));

  const dispatch = useDispatch();

  const srAPI: UseServiceRequestAPIReturn = useServiceRequestAPI();

  const subscribersAPI: UseServiceRequestSubscribersReturn =
    useServiceRequestSubscribers(serviceRequestId);

  const ticketStatusesAPI: UseTicketStatusesReturn = useTicketStatuses();

  const modulePermissionsAPI: UseModulePermissionsAPIReturn =
    useModulePermissionsAPI();

  const [ui, setUi] = useState<ServiceRequestUIState>({
    showEditOverlay: false,
    showAssignOverlay: false,
    showScheduleOverlay: false,
    showDeclinePopup: false,
  });

  const refreshData = async (): Promise<void> => {
    if (!user || !serviceRequestId) {
      return;
    }

    srAPI.clearServiceRequestState();

    const fetched: any = await srAPI.fetchServiceRequestById(serviceRequestId);

    await srAPI.fetchServiceRequestActivity(serviceRequestId);

    if (fetched?.propertyId) {
      await modulePermissionsAPI.onFetchModulePermissions(fetched.propertyId);
    }
  };

  const openEditOverlay = (): void => {
    setUi((prev: ServiceRequestUIState) => ({
      ...prev,
      showEditOverlay: true,
    }));
  };

  const closeEditOverlay = (): void => {
    setUi((prev: ServiceRequestUIState) => ({
      ...prev,
      showEditOverlay: false,
    }));
  };

  const closeAssignOverlay = (): void => {
    setUi((prev: ServiceRequestUIState) => ({
      ...prev,
      showAssignOverlay: false,
    }));
  };

  const closeScheduleOverlay = (): void => {
    setUi((prev: ServiceRequestUIState) => ({
      ...prev,
      showScheduleOverlay: false,
    }));
  };

  const handleEditFormSaved = (): void => {
    setUi((prev: ServiceRequestUIState) => ({
      ...prev,
      showEditOverlay: false,
    }));
    setTimeout(() => refreshData(), 300);
  };

  const handleDeleteServiceRequest = async (): Promise<void> => {
    if (!srAPI.serviceRequest?.id) {
      toast.error('Invalid service request data');
      return;
    }

    const success: boolean = await srAPI.deleteServiceRequestFromAPI(
      srAPI.serviceRequest.id
    );

    if (!success) {
      return;
    }

    onAfterDelete();
  };

  const updateStatusWithUser = async (
    statusFormData: any,
    srId: string
  ): Promise<boolean> => {
    if (ticketStatusesAPI.ticketStatuses.length === 0) {
      console.error('Invalid ticket status');
      toast.error('Invalid ticket request data. Please try again. If this issue persists please contact our support');
      return false;
    }

    const status: TicketStatus | undefined = ticketStatusesAPI.ticketStatuses.find(
      (s: TicketStatus) => s.name === statusFormData.name
    );

    if (!status) {
      return false;
    }

    const statusData = {
      ...statusFormData,
      LoginUserId: user.id,
      TicketStatusId: status.id,
    };

    return await srAPI.updateStatus(statusData, srId);
  };

  const handleStatusChange = async (
    statusFormData: any,
    srId: string
  ): Promise<void> => {
    if (statusFormData.name === 'Assigned') {
      setUi((prev: ServiceRequestUIState) => ({
        ...prev,
        showAssignOverlay: true,
      }));
      return;
    }

    if (statusFormData.name === 'Scheduled') {
      setUi((prev: ServiceRequestUIState) => ({
        ...prev,
        showScheduleOverlay: true,
      }));
      return;
    }

    if (statusFormData.name === 'Declined') {
      setUi((prev: ServiceRequestUIState) => ({
        ...prev,
        showDeclinePopup: true,
      }));
      return;
    }

    if (statusFormData.name === 'Archive') {
      const role: string = user.roles[0].role.name;
      const archiveParams = {
        serviceRequest: [{ id: srId }],
        name: statusFormData.name,
        userId: user.id,
      };
      dispatch(
        updateBulkStatus(archiveParams, role, 'params1', 'serviceRequest')
      );
      return;
    }

    await updateStatusWithUser(statusFormData, srId);
  };

  const handleStatusAssign = async (
    statusFormData: any,
    srId: string
  ): Promise<void> => {
    await updateStatusWithUser(
      { name: 'Assigned', ServiceProviderId: statusFormData.userId },
      srId
    );
    setUi((prev: ServiceRequestUIState) => ({
      ...prev,
      showAssignOverlay: false,
    }));
  };

  const onSubmitDeclinePopup = async (
    type: string,
    data: any
  ): Promise<void> => {
    if (type === 'cancel') {
      setUi((prev: ServiceRequestUIState) => ({
        ...prev,
        showDeclinePopup: false,
      }));
      return;
    }

    setUi((prev: ServiceRequestUIState) => ({
      ...prev,
      showDeclinePopup: false,
    }));

    await updateStatusWithUser(
      {
        name: 'Declined',
        Reason: data.description,
        ReasonType: data.reason,
      },
      serviceRequestId
    );
  };

  const handleSaveScheduledAppointment = async (
    formData: any,
    srId: string
  ): Promise<void> => {
    const status: TicketStatus | undefined = ticketStatusesAPI.ticketStatuses.find(
      (s: TicketStatus) => s.name === formData.name,
    );

    const appointmentData = {
      ...formData,
      LoginUserId: user.id,
      ticketId: srAPI.serviceRequest.ticketId,
      userId: srAPI.serviceRequest.serviceProvider.id,
      TicketStatusId: status?.id,
    };

    const saved: boolean = await srAPI.saveScheduledAppointmentFromAPI(
      appointmentData,
      srId
    );

    if (!saved) {
      return;
    }

    closeScheduleOverlay();
  };

  const downloadPdf = async (id: string): Promise<void> => {
    // Open the tab synchronously within the click gesture so popup blockers don't
    // reject it after the await. We navigate to the S3 URL directly rather than
    // using downloadjs, whose cross-origin XHR is blocked by S3 CORS on some envs.
    const pdfWindow: Window | null = window.open('', '_blank');

    if (pdfWindow) {
      pdfWindow.opener = null;
    }

    const failDownload = () => {
      pdfWindow?.close();
      toast.error('Failed to download PDF.');
    };

    try {
      const response: any = await getServiceRequestPdfById(id);

      if (!response.ok && response.status !== 200) {
        failDownload();
        return;
      }

      const rawText: string = await response.text();
      const pdfUrl: string = rawText.replace(/^"|"$/g, '');

      if (pdfWindow) {
        pdfWindow.location.href = pdfUrl;
      } else {
        window.open(pdfUrl, '_blank', 'noopener');
      }
    } catch {
      failDownload();
    }
  };

  useEffect(() => {
    refreshData();
  }, [serviceRequestId, user]);

  const isCurrentUserSubscribed: boolean = subscribersAPI.subscribers.some(
    (subscriber: Subscriber) => subscriber.userId === user?.id,
  );

  const homeOwnerDataVisibility: boolean =
    modulePermissionsAPI.modulePermissions.some(
      (permission: ModulePermission) =>
        permission.name === HOME_OWNER_DATA_VISIBILITY_MODULE
    );

  return {
    user,
    serviceRequest: srAPI.serviceRequest,
    activity: srAPI.activity,
    loading: srAPI.loading,
    error: srAPI.error,
    oneYearWarrantyIssues: srAPI.oneYearWarrantyIssues,
    homeOwnerDataVisibility,

    showEditOverlay: ui.showEditOverlay,
    showAssignOverlay: ui.showAssignOverlay,
    showScheduleOverlay: ui.showScheduleOverlay,
    showDeclinePopup: ui.showDeclinePopup,

    openEditOverlay,
    closeEditOverlay,
    closeAssignOverlay,
    closeScheduleOverlay,

    refreshData,
    fetchServiceRequestById: srAPI.fetchServiceRequestById,
    handleDeleteServiceRequest,
    handleStatusChange,
    handleStatusAssign,
    onSubmitDeclinePopup,
    handleEditFormSaved,
    handleSaveScheduledAppointment,
    downloadPdf,

    subscribers: subscribersAPI.subscribers,
    subscribersLoading: subscribersAPI.loading,
    subscribersSaving: subscribersAPI.saving,
    saveSubscribers: subscribersAPI.saveSubscribers,
    isCurrentUserSubscribed,
  };
};
